import type { ReactNode } from 'react';
import { cx } from '../primitives/cx.js';

/**
 * The page container.
 *
 * One maximum width, centred, with the same gutters on every screen. Before
 * this, each screen decided for itself and most of them decided nothing, so
 * content sat in a fixed-width block at the top left of a wide window with
 * forty per cent of the page empty beside it.
 */
export function PageContainer({
  children,
  className,
  ...rest
}: { children: ReactNode; className?: string } & Record<string, unknown>): JSX.Element {
  return (
    <div {...rest} data-component="PageContainer" className={cx('mx-auto w-full max-w-content', className)}>
      {children}
    </div>
  );
}

/**
 * The page grid.
 *
 * One gap everywhere. Two-column pages give the main column the larger share
 * and collapse to one below the large breakpoint - a two-column layout at
 * tablet width is two columns too narrow to read rather than one that works.
 */
export function Grid({
  columns = 'quad',
  children,
  className,
  ...rest
}: {
  /**
   * `quad` for four equal figures, `main-aside` for work beside a companion,
   * `two-one` where the companion carries more.
   */
  columns?: 'quad' | 'main-aside' | 'two-one';
  children: ReactNode;
  className?: string;
} & Record<string, unknown>): JSX.Element {
  const shape =
    columns === 'quad'
      ? 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-quad'
      : columns === 'main-aside'
        ? 'grid-cols-1 lg:grid-cols-main-aside'
        : 'grid-cols-1 lg:grid-cols-two-one';
  return (
    <div {...rest} data-component="Grid" className={cx('grid gap-5', shape, className)}>
      {children}
    </div>
  );
}

/**
 * A card, its header, and its body.
 *
 * The header's label is the quiet uppercase one; the loud thing on a card is
 * its content. A link sits at the end of the header rather than the bottom of
 * the card, because "see all of these" belongs beside the name of the thing
 * rather than after an unpredictable amount of it.
 */
export function Card({
  children,
  className,
  ...rest
}: { children: ReactNode; className?: string } & Record<string, unknown>): JSX.Element {
  return (
    <section
      {...rest}
      data-component="Card"
      className={cx('overflow-hidden rounded-panel border border-rule bg-surface', className)}
    >
      {children}
    </section>
  );
}

export function CardHeader({
  title,
  action,
  className,
  ...rest
}: { title: ReactNode; action?: ReactNode; className?: string } & Record<string, unknown>): JSX.Element {
  return (
    <div
      {...rest}
      data-component="CardHeader"
      className={cx('flex items-center gap-2 px-5 pb-3 pt-4', className)}
    >
      <h2 className="text-micro uppercase tracking-label text-ink-label">{title}</h2>
      {action === undefined ? null : <div className="ms-auto text-dense">{action}</div>}
    </div>
  );
}

export function CardBody({
  flush = false,
  children,
  className,
  ...rest
}: {
  /** Rows run to the card's edge; padding would break their hairlines. */
  flush?: boolean;
  children: ReactNode;
  className?: string;
} & Record<string, unknown>): JSX.Element {
  return (
    <div
      {...rest}
      data-component="CardBody"
      className={cx(flush ? 'p-0' : 'px-5 pb-5', className)}
    >
      {children}
    </div>
  );
}

/**
 * A row inside a card.
 *
 * A grid with declared columns, never `space-between`. On a 1,360px card
 * space-between puts most of a metre between a label and its value, and a
 * reader has to track across the gap for every row - which is the difference
 * between a table somebody can scan and a list of facts they have to read one
 * at a time.
 */
export function Row({
  columns = 'work',
  children,
  className,
  ...rest
}: {
  columns?: 'work' | 'step' | 'stage';
  children: ReactNode;
  className?: string;
} & Record<string, unknown>): JSX.Element {
  return (
    <div
      {...rest}
      data-component="Row"
      className={cx(
        'group grid items-center gap-4 border-t border-t-rule px-5 py-3 first:border-t-0',
        'transition-colors duration-state ease-out hover:bg-sunken',
        columns === 'work'
          ? 'grid-cols-row-work'
          : columns === 'step'
            ? 'grid-cols-row-step'
            : 'grid-cols-row-stage',
        className,
      )}
    >
      {children}
    </div>
  );
}
